import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, Table, Button, Modal, Form, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { useParams, useNavigate } from 'react-router-dom';
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { getRequest } from 'services/api';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import 'dayjs/locale/id';

const formatPrice = (price) => {
  if (price === undefined || price === null) return "0";
  return new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(price);
};

const AgentHistoryMonthGroup = ({ agentId, startDate, endDate, monthName, activeAgentName, openDetailModal }) => {
  const [historyData, setHistoryData] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [autoLoad, setAutoLoad] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const loaderRef = useRef(null);

  useEffect(() => {
    if (!autoLoad || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const nextPage = page + 1;
          setPage(nextPage);
          fetchData(nextPage, false);
        }
      },
      { rootMargin: '100px' }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [autoLoad, hasMore, loading, page, agentId, startDate, endDate]);


  useEffect(() => {
    setHistoryData([]);
    setPage(1);
    setHasMore(true);
    fetchData(1, true);
  }, [agentId, startDate, endDate]);

  const fetchData = async (pageNum, reset = false, isAutoLoad = false) => {
    if (!agentId) return;
    setLoading(true);
    try {
      const res = await getRequest(`/reservation-income-v2?agent_id=${agentId}&start_date=${startDate}&end_date=${endDate}&page=${pageNum}&paginate=10&orderBy=reservation_date&sort=asc`);
      
      const rawData = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []);
      
      if (rawData.length > 0) {
        const normalized = rawData.map(row => {
          let singleQty = 0;
          let tandemQty = 0;
          row.activities?.forEach(act => {
            if (act.activity === "ATV Single") singleQty += act.qty;
            if (act.activity === "ATV Tandem") tandemQty += act.qty;
          });
          const qtyStr = [];
          if (singleQty > 0) qtyStr.push(`${singleQty} SINGLE`);
          if (tandemQty > 0) qtyStr.push(`${tandemQty} TANDEM`);

          const typeStr = row.type ? `/${row.type.toUpperCase()}` : '';
          const guestTransport = row.guest_transport ? `/${row.guest_transport.toUpperCase()}` : '';
          const customerName = row.guest_name ? `/${row.guest_name.toUpperCase()}` : '';
          const hotel = row.hotel ? `/${row.hotel.toUpperCase()}` : '';
          const agentName = row.agent_name ? `/${row.agent_name.toUpperCase()}` : (row.agent?.name ? `/${row.agent.name.toUpperCase()}` : '');

          const transaksiText = `ATV ${qtyStr.join(',')}${typeStr}${guestTransport}${customerName}${hotel}${agentName}`;

          return {
            ...row,
            transaksiText,
            formattedDate: dayjs(row.reservation_date).format('DD-MMM-YY').toUpperCase(),
            bayar: Number(row.payment_bill) || 0,
            netAgent: Number(row.net_agent) || 0,
            susukAgent: Number(row.susuk_agent) || 0,
            net: Number(row.net_kuber) || 0,
            susukJosh: Number(row.susuk_agent) || 0,
            paid: row.is_paid || false 
          };
        });

        normalized.sort((a, b) => dayjs(a.reservation_date).diff(dayjs(b.reservation_date)));

        if (reset) {
          setHistoryData(normalized);
        } else {
          setHistoryData(prev => [...prev, ...normalized].sort((a, b) => dayjs(a.reservation_date).diff(dayjs(b.reservation_date))));
        }
        
        if (res?.data?.total !== undefined) {
           setTotalCount(res.data.total);
        }

        if (rawData.length < 10 || (res?.data?.last_page && pageNum >= res.data.last_page)) {
          setHasMore(false);
          setAutoLoad(false);
        }
      } else {
        setHasMore(false);
        setAutoLoad(false);
      }
    } catch (error) {
      console.error("Error fetching history month group", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    setAutoLoad(true);
    // Intersection observer will trigger the fetch immediately since the loader is in view
  };

  if (historyData.length === 0 && !loading) {
    return null;
  }

  return (
    <Card className="shadow-sm border-0 mb-4 pb-1">
      <div className="d-flex justify-content-between align-items-center bg-light p-3 border-bottom">
        <h5 className="fw-bold m-0" style={{ color: '#000' }}>{monthName}</h5>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <Table size="sm" bordered hover responsive style={{ minWidth: '1200px', margin: 0, border: '2px solid #000', fontSize: '12px', whiteSpace: 'nowrap' }}>
          <thead>
            <tr style={{ backgroundColor: '#ffff00' }}>
              <th colSpan={2} className="text-black fw-bold border-dark align-middle px-2 py-1" style={{ backgroundColor: '#ffff00', fontSize: '14px', padding: '4px 8px' }}>
                VOUCHERS/MINUS {monthName}
              </th>
              <th className="text-center border-dark" style={{ width: '150px' }}>BAYAR</th>
              <th className="text-center border-dark" style={{ width: '150px' }}>NET AGENT</th>
              <th className="text-center border-dark" style={{ width: '150px' }}>SUSUK AGENT</th>
              <th className="text-center border-dark" style={{ width: '150px' }}>NET</th>
              <th className="text-center border-dark" style={{ width: '150px' }}>SUSUK {activeAgentName.toUpperCase()}</th>
            </tr>
          </thead>
          <tbody>
            {historyData.map((row) => (
              <tr key={row.id}>
                <td className="fw-bold text-center border-dark align-middle px-2 py-1" style={{ backgroundColor: '#cc0000', color: 'white', width: '100px' }}>
                  {row.formattedDate}
                </td>
                <td 
                  className="border-dark text-primary text-decoration-underline align-middle px-2 py-1" 
                  style={{ cursor: 'pointer' }}
                  onClick={() => openDetailModal(row)}
                >
                  {row.transaksiText}
                </td>
                <td className="text-end border-dark align-middle px-2 py-1">{formatPrice(row.bayar)}</td>
                <td className="text-end border-dark align-middle px-2 py-1">{formatPrice(row.netAgent)}</td>
                <td className="text-end fw-bold border-dark align-middle px-2 py-1" style={{ color: row.susukAgent > 0 ? '#cc0000' : '#000' }}>
                  {formatPrice(row.susukAgent)}
                </td>
                <td className="text-end border-dark align-middle px-2 py-1">{formatPrice(row.net)}</td>
                <td className="text-end border-dark align-middle px-2 py-1">{formatPrice(row.susukJosh)}</td>
              </tr>
            ))}
            
            <tr className="fw-bold text-black border-dark" style={{ backgroundColor: '#c6e0b4' }}>
              <td colSpan={2} className="text-center border-dark">TOTAL {monthName} (SEMUA DATA)</td>
              <td className="text-end border-dark">{formatPrice(historyData.reduce((s, r) => s + r.bayar, 0))}</td>
              <td className="text-end border-dark">{formatPrice(historyData.reduce((s, r) => s + r.netAgent, 0))}</td>
              <td className="text-end border-dark text-danger">{formatPrice(historyData.reduce((s, r) => s + r.susukAgent, 0))}</td>
              <td className="text-end border-dark">{formatPrice(historyData.reduce((s, r) => s + r.net, 0))}</td>
              <td className="text-end border-dark">{formatPrice(historyData.reduce((s, r) => s + r.susukJosh, 0))}</td>
            </tr>
          </tbody>
        </Table>
      </div>
      
      {hasMore && (
        <div className="text-center p-3" ref={loaderRef}>
          {!autoLoad ? (
            <Button variant="light" size="sm" onClick={handleLoadMore} className="fw-bold border shadow-sm">
              <i className="fe fe-arrow-down me-1"></i> Load All Remaining ({totalCount > historyData.length ? totalCount - historyData.length : "?"} data)
            </Button>
          ) : (
            <div className="text-muted small fw-bold">
              <Spinner animation="border" size="sm" className="me-2" /> 
              Loading data... {historyData.length} / {totalCount || "?"}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
const AgentHistoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);

  // Detail Modal State
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  // Filter States
  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(5, 'month').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD')
  });

  const [fetchTrigger, setFetchTrigger] = useState(0);
  
  // Month Intervals state
  const [monthIntervals, setMonthIntervals] = useState([]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilter = () => {
    setFetchTrigger(prev => prev + 1);
  };

  useEffect(() => {
    if (!id) return;
    const start = dayjs(filters.startDate);
    const end = dayjs(filters.endDate);
    
    if (start.isAfter(end)) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Start date cannot be after end date' });
      return;
    }
    
    const intervals = [];
    let current = start.startOf('month');
    while (current.isBefore(end) || current.isSame(end, 'month')) {
      let s = current.isSame(start, 'month') ? start : current.startOf('month');
      let e = current.isSame(end, 'month') ? end : current.endOf('month');
      
      intervals.push({
        name: current.format('MMMM YYYY').toUpperCase(),
        start: s.format('YYYY-MM-DD'),
        end: e.format('YYYY-MM-DD')
      });
      current = current.add(1, 'month');
    }
    
    setMonthIntervals(intervals);
  }, [id, fetchTrigger]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await getRequest('/agent?limit=1000');
        if (res?.data) {
          setAgents(res.data);
        }
      } catch (error) {
        console.error("Error fetching agents", error);
      }
    };
    fetchAgents();
  }, []);

  const activeAgent = useMemo(() => agents.find(a => a.id === id) || { name: 'AGENT' }, [agents, id]);
  const activeAgentName = activeAgent.name;

  const openDetailModal = (row) => {
    setSelectedDetail(row);
    setShowDetailModal(true);
  };

  return (
    <LayoutDashboard>
      <div className="container-fluid pt-3 pb-5 mb-5">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold m-0">HISTORY - {activeAgentName.toUpperCase()}</h4>
            <div className="text-muted small mt-1">Data riwayat transaksi berdasarkan periode tanggal</div>
          </div>
        </div>

        <Card className="shadow-sm border-0 mb-4 bg-light">
          <Card.Body className="p-3">
            <div className="row g-2 align-items-end">
              <div className="col-md-3">
                <Form.Label className="small fw-bold mb-1">Start Date</Form.Label>
                <Form.Control type="date" size="sm" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
              </div>
              <div className="col-md-3">
                <Form.Label className="small fw-bold mb-1">End Date</Form.Label>
                <Form.Control type="date" size="sm" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
              </div>
              <div className="col-md-2">
                <Button variant="primary" size="sm" className="w-100 mt-4" onClick={handleApplyFilter}>
                  <i className="fe fe-filter me-1"></i> Terapkan
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {monthIntervals.map((interval, idx) => (
          <AgentHistoryMonthGroup 
            key={interval.name + idx}
            agentId={id}
            startDate={interval.start}
            endDate={interval.end}
            monthName={interval.name}
            activeAgentName={activeAgentName}
            openDetailModal={openDetailModal}
          />
        ))}
        
        {monthIntervals.length === 0 && (
          <div className="text-center text-muted p-5 bg-light rounded border border-dashed">
            Klik Terapkan untuk memuat data.
          </div>
        )}

        <div 
          className="d-flex align-items-center bg-light shadow-lg" 
          style={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1000, 
            overflowX: 'auto', 
            borderTop: '2px solid #ccc'
          }}
        >
          <div style={{ minWidth: '250px', borderRight: '1px solid #ccc', padding: '5px 10px', backgroundColor: '#e2e3e5' }}>
            <Select 
              options={agents.map(a => ({ value: a.id, label: a.name }))}
              placeholder="Search Agent..."
              value={agents.find(a => a.id === id) ? { value: id, label: agents.find(a => a.id === id).name } : null}
              onChange={(selected) => {
                if (selected) navigate(`/v1/agents/${selected.value}/history`);
              }}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              menuPortalTarget={document.body}
              menuPlacement="top"
            />
          </div>
          {agents.map((agent) => (
            <OverlayTrigger
              key={agent.id}
              placement="top"
              overlay={<Tooltip id={`tooltip-${agent.id}`}>{agent.name}</Tooltip>}
            >
              <div 
                onClick={() => navigate(`/v1/agents/${agent.id}/history`)}
                style={{
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontWeight: id === agent.id ? 'bold' : 'normal',
                  backgroundColor: id === agent.id ? '#fff' : '#f8f9fa',
                  borderRight: '1px solid #ccc',
                  borderBottom: id === agent.id ? '3px solid #0d6efd' : '3px solid transparent',
                  minWidth: '100px',
                  maxWidth: '120px',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {agent.name.toUpperCase()}
              </div>
            </OverlayTrigger>
          ))}
          {agents.length === 0 && (
            <div className="px-3 text-muted small">Loading agents...</div>
          )}
        </div>

        <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Payment EDC Detail</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedDetail && (
              <div className="mb-3 p-3 bg-light rounded border">
                <p className="mb-1 text-primary fw-bold" style={{ fontSize: '13px' }}>
                  {selectedDetail.transaksiText || selectedDetail.code}
                </p>
                <div className="d-flex justify-content-between mt-2 pt-2 border-top">
                  <span className="text-muted small">Payment Bill:</span>
                  <span className="fw-bold">{formatPrice(selectedDetail.bayar)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted small">Net Agent:</span>
                  <span className="fw-bold">{formatPrice(selectedDetail.netAgent)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted small">Susuk Agent:</span>
                  <span className="fw-bold text-danger">{formatPrice(selectedDetail.susukAgent)}</span>
                </div>
              </div>
            )}
            
            <p className="text-muted small text-center mt-4">
              Detail ini hanya untuk melihat ringkasan transaksi history agent.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)}>Tutup</Button>
            {selectedDetail && (selectedDetail.agent_id || selectedDetail.agent?.id) && (
              <Button 
                variant="warning" 
                size="sm" 
                onClick={() => {
                  const agentId = selectedDetail.agent_id || selectedDetail.agent?.id;
                  window.open(`/v1/agents/${agentId}/history`, '_blank');
                }}
              >
                <i className="fe fe-file-text me-1"></i> Keep
              </Button>
            )}
          </Modal.Footer>
        </Modal>

      </div>
    </LayoutDashboard>
  );
};

export default AgentHistoryPage;
